import { useEffect, useId, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ThemeToggle";
import { BrandLogoHorizontalNavy, BrandLogoHorizontalWhite } from "@/assets/brand";
import { Link } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";
import { useTheme } from "next-themes";
import { ChevronDown, Menu, X, Globe } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useDemoDialog } from "@/components/DemoRequestProvider";
import { buildMainNav } from "@/config/navigation";


const Header = () => {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const { open: openDemo } = useDemoDialog();
  const { resolvedTheme } = useTheme();
  const logo = resolvedTheme === "dark" ? BrandLogoHorizontalWhite : BrandLogoHorizontalNavy;
  const menuIdBase = useId();
  const mobileNavId = `${menuIdBase}-mobile`;
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Main navigation comes from a single shared config (see src/config/navigation.ts).
  const menuItems = buildMainNav(t, language);


  const closeMenu = (restoreFocusTo?: string | null) => {
    setOpenMenu(null);
    if (restoreFocusTo) {
      // Restore focus to the trigger that opened the menu
      requestAnimationFrame(() => triggerRefs.current[restoreFocusTo]?.focus());
    }
  };

  // Global Esc handler — closes any open desktop menu / mobile menu
  useEffect(() => {
    if (!openMenu && !mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (openMenu) closeMenu(openMenu);
        if (mobileOpen) setMobileOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openMenu, mobileOpen]);

  return (
    <header className="fixed left-0 right-0 z-50" style={{ top: "var(--announcement-h, 0px)" }}>
      <div className="bg-white dark:bg-background/70 backdrop-blur-xl border-b border-border/60">
        <div className="enterprise-container">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 shrink-0 rounded-sm" aria-label="IBYLIT — Inicio">
              <img src={logo} alt="" aria-hidden="true" loading="eager" decoding="async" width={144} height={50} className="h-9 md:h-10 w-auto" />
            </Link>

            <nav className="hidden lg:flex items-center gap-1" aria-label={t.a11y.mainNavigation}>
              {menuItems.map((item) => {
                const panelId = `${menuIdBase}-${item.label.replace(/\s+/g, "-")}`;
                const isOpen = openMenu === item.label;
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => setOpenMenu(item.label)}
                    onMouseLeave={() => setOpenMenu(null)}
                  >
                    <button
                      ref={(el) => (triggerRefs.current[item.label] = el)}
                      type="button"
                      aria-expanded={isOpen}
                      aria-haspopup="menu"
                      aria-controls={isOpen ? panelId : undefined}
                      onClick={() => setOpenMenu(isOpen ? null : item.label)}
                      onKeyDown={(e) => {
                        if (e.key === "Escape" && isOpen) {
                          e.preventDefault();
                          closeMenu(item.label);
                        }
                        if ((e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") && !isOpen) {
                          e.preventDefault();
                          setOpenMenu(item.label);
                        }
                      }}
                      className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors rounded-sm"
                    >
                      {item.label}
                      <ChevronDown className="w-3 h-3" aria-hidden="true" />
                    </button>
                    {isOpen && (
                      <div className="absolute top-full left-0 pt-2 z-50">
                        <div
                          id={panelId}
                          role="menu"
                          aria-label={item.label}
                          className="bg-popover/95 backdrop-blur-xl border border-border rounded-2xl shadow-2xl py-2 min-w-[240px] overflow-hidden"
                        >
                          <div className="rail-line" aria-hidden="true" />
                          {item.children.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              role="menuitem"
                              className="block px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors focus-visible:bg-muted focus-visible:text-foreground focus:outline-none"
                              onClick={() => closeMenu(item.label)}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

            </nav>

            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={toggleLanguage}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                aria-label={`${language === "en" ? "ES" : "EN"} — ${t.a11y.toggleLanguage}`}
                title={language === "en" ? "Cambiar a Español" : "Switch to English"}
              >
                <Globe className="w-3.5 h-3.5" aria-hidden="true" />
                {language === "en" ? "ES" : "EN"}
              </button>
              <ThemeToggle />
              <Button variant="outline" size="sm" className="hidden xl:inline-flex" onClick={openDemo}>
                {language === "en" ? "Talk to IBYLIT" : "Hablar con IBYLIT"}
              </Button>
              <Button asChild variant="default" size="sm" className="hidden md:inline-flex">
                <Link to="/lanzamiento-fondea" onClick={() => trackEvent("fondea_launch_menu_click", { source: "header_cta" })}>
                  {language === "en" ? "Enter FONDEA" : "Entrar a FONDEA"}
                </Link>
              </Button>
              <button
                type="button"
                className="lg:hidden p-2 text-muted-foreground hover:text-foreground"
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-expanded={mobileOpen}
                aria-controls={mobileNavId}
                aria-label={mobileOpen ? t.a11y.closeMenu : t.a11y.openMenu}
              >
                {mobileOpen ? <X className="w-5 h-5" aria-hidden="true" /> : <Menu className="w-5 h-5" aria-hidden="true" />}
              </button>
            </div>
          </div>
        </div>
        {/* Bottom rail — IBYLIT signature */}
        <div className="rail-line" aria-hidden="true" />
      </div>

      {mobileOpen && (
        <nav
          id={mobileNavId}
          aria-label={t.a11y.mainNavigation}
          className="lg:hidden bg-background/95 backdrop-blur-xl border-b border-border max-h-[80vh] overflow-y-auto shadow-2xl"
        >
          <div className="enterprise-container py-4 space-y-2">
            {menuItems.map((item) => (
              <details key={item.label} className="group">
                <summary className="flex items-center justify-between px-3 py-2 text-sm font-medium text-foreground cursor-pointer list-none">
                  {item.label}
                  <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" aria-hidden="true" />
                </summary>
                <div className="pl-4 mt-1 space-y-1">
                  {item.children.map((child) => (
                    <Link
                      key={child.href}
                      to={child.href}
                      className="block px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
                      onClick={() => setMobileOpen(false)}
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              </details>
            ))}
            <div className="pt-2">
              <Button asChild variant="default" size="sm" className="w-full">
                <Link to="/lanzamiento-fondea" onClick={() => setMobileOpen(false)}>
                  {language === "en" ? "Enter FONDEA" : "Entrar a FONDEA"}
                </Link>
              </Button>
              <Button variant="outline" size="sm" className="mt-2 w-full" onClick={() => { setMobileOpen(false); openDemo(); }}>
                {language === "en" ? "Talk to IBYLIT" : "Hablar con IBYLIT"}
              </Button>
            </div>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
